/**
 * 案件の変更の伝播 — レーン 4 型（ステージ・業務フロー・業務・業務ルール）の
 * 「変わった」を配下から上へ伝える。
 *
 * 案件の変更の行は、その案件が**直接**書き換えたノードにしか立たない。業務ルールだけを足した
 * 案件では業務の行が立たず、業務だけを変えた案件では業務フローもステージも行が
 * 立たない。行だけで印を付けると「ステージは何も変わっていない」と読めてしまうが、読み手に
 * とっては配下が変わったステージは変わったステージである。そこで状態を 3 値で持つ。
 *
 * | 状態 | 意味 | 値 |
 * |---|---|---|
 * | 直接 | そのノード自身の変更の行が在る | `created` / `updated` / `deleted` |
 * | 配下 | 自身の行は無いが、配下のどれかが直接か配下 | `descendant` |
 * | 変更なし | どちらでもない | 返さない（キーごと持たない） |
 *
 * 配下の定義は階層の 1 段下である。
 *
 * | 親 | 配下 | 引くエッジ |
 * |---|---|---|
 * | ステージ | そこに属する業務フロー | `BELONGS_TO_STAGE`（業務フロー → ステージ） |
 * | 業務フロー | 節点が指す業務 | `INCLUDES_BUSINESS`（業務フロー → 業務） |
 * | 業務 | `rules` の業務ルール | `USES_RULE`（業務 → 業務ルール） |
 *
 * ステージどうしの並び（鎖）は親子ではないので辿らない。前のステージが変わっても、後ろの
 * ステージの配下が変わったことにはならない。
 *
 * **関係は master と案件の両方の姿から取る。** 案件が足した配下（新しいステージ・業務フロー・
 * 業務・業務ルール）は案件を当てた姿にしか居らず、消した配下は master にしか居ない。どちらか
 * 片方だけを見ると、足した業務ルールしか持たない業務も、業務を 1 つ消されただけの業務フローも
 * 「変更なし」になる。master のエッジに、案件の変更の行の前後の値から導いたエッジを足した
 * 和集合を辿る。
 *
 * **ここが判定の唯一の場所である。** 画面は返った状態を読むだけで、階層を自分で辿らない。
 * 業務マップと案件の変更一覧が別々に辿ると、同じ案件の同じステージが画面によって変わったり
 * 変わらなかったりする。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更の行と階層のエッジから、ステージ・業務フロー・業務・業務ルールの変更の種類（直接の追加 / 変更 / 削除と配下に変更）を決める
 * @graph-connects bigquery.spec_graph_edges [reads_from, via:edgeType] 階層のエッジ 3 本の読取
 */
import { SPEC_ELEMENT_TABLE_IDS, } from '../../contract/elements/index.js';
import { decodeElementValue } from '../../core/elements/index.js';
import { asRecord, runReadQuery, tableRef } from '../read-client.js';
import { deriveBusinessMapEdges } from './business-map.js';
/**
 * 伝播の対象になるノードの型 4 種。並びは階層の上から下である。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 変更の伝播の対象になるノード型 4 種（ステージ・業務フロー・業務・業務ルール）の閉集合
 * @graph-connects none
 */
export const PJ_LANE_NODE_TYPES = [
    'Stage',
    'Flow',
    'Business',
    'Rule',
];
/**
 * 変更の種類 4 値。前の 3 つが直接の変更、`descendant` が配下の変更である。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 変更の種類 4 値（直接の追加・変更・削除と、配下に変更）の閉集合
 * @graph-connects none
 */
export const PJ_LANE_CHANGE_KINDS = ['created', 'updated', 'deleted', 'descendant'];
/**
 * 階層を辿るエッジの型 3 種。並びは階層の上から下である。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 変更の伝播が辿るエッジ型 3 種（所属ステージ・含む業務・使う業務ルール）の閉集合
 * @graph-connects none
 */
export const PJ_LANE_EDGE_TYPES = [
    'BELONGS_TO_STAGE',
    'INCLUDES_BUSINESS',
    'USES_RULE',
];
/**
 * 案件の変更 1 行の直接の変更の種類を決める。
 *
 * | 行の前後 | 種類 |
 * |---|---|
 * | 変更前が無い | 追加 |
 * | 変更後が無い | 削除 |
 * | 両方ある | 変更 |
 *
 * 前後とも無い行は作られない（変化の無いノードは行を作らない）。それでも来たら `null` を返し、
 * 変わったことにしない。何が起きたか読めない行に印を付けると、読み手に嘘の手掛かりを渡す。
 *
 * @param row 案件の変更 1 行の前後
 * @returns 直接の変更の種類。読めない行は `null`
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更 1 行の前後の有無から、直接の変更の種類（追加・変更・削除）を決める
 * @graph-connects none
 */
export function directChangeKind(row) {
    if (row.before === null && row.after === null)
        return null;
    if (row.before === null)
        return 'created';
    if (row.after === null)
        return 'deleted';
    return 'updated';
}
/** その型が伝播の対象かを見る。 @graph-connects none */
function laneTypeOf(nodeType) {
    return PJ_LANE_NODE_TYPES.find((value) => value === nodeType) ?? null;
}
/**
 * 行の前後の値を読む。**読めない値は投げる**（`decodeElementValue` と同じ立場）。
 * @graph-connects none
 */
function rowValues(row) {
    const subject = `案件 ${String(row.pjNumber)} のノード ${String(row.nodeId)}`;
    return [row.before, row.after].flatMap((json) => json === null ? [] : [decodeElementValue(subject, json)]);
}
/**
 * 案件の変更の行から、直接変わったレーン 4 型のノードを拾う。
 *
 * 型は変更後の値にあり、削除された行は変更前の値にしかない。型の読めない行と、レーンの 4 型の
 * 外（用語・画面・部品など）の行は拾わない — 4 段の階層に載らない型は伝播の起点にならない。
 *
 * @param rows 案件の変更の行
 * @returns 直接変わったノード（行の順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更の行から、直接変わった ステージ・業務フロー・業務・業務ルールを種類つきで拾う
 * @graph-connects none
 */
export function directPjLaneChanges(rows) {
    return rows.flatMap((row) => {
        const changeKind = directChangeKind(row);
        const value = rowValues(row).at(-1);
        const nodeType = value === undefined ? null : laneTypeOf(value.nodeType);
        return changeKind === null || nodeType === null
            ? []
            : [{ nodeId: row.nodeId, nodeType, changeKind }];
    });
}
/**
 * 案件の変更の行の前後の値から、階層のエッジを導く。
 *
 * 前も後も導くのは、案件が外した配下（前にしか無い）と足した配下（後にしか無い）の両方から
 * 親へ辿れるようにするためである。
 *
 * @param rows 案件の変更の行
 * @returns 前後の値から導いた階層のエッジ
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更の行の前後の値から、所属ステージ・含む業務・使う業務ルールのエッジを導く
 * @graph-connects deriveBusinessMapEdges [calls] 業務フロー・業務の欄からエッジを導く
 */
export function pjLaneEdgesOfRows(rows) {
    return rows.flatMap((row) => rowValues(row).flatMap((value) => deriveBusinessMapEdges(new Map([[row.nodeId, value]]), [row.nodeId]).filter((edge) => PJ_LANE_EDGE_TYPES.includes(edge.edgeType))));
}
/**
 * エッジを「配下の id → 親」の表に組む。
 *
 * 業務フロー → ステージのエッジは起点が配下である（所属は子が親を指す）。
 * 含む業務と使う業務ルールは起点が親である。向きの違いをここで吸収し、辿る側は 1 通りの問いにする。
 * @graph-connects none
 */
function parentsByChild(edges) {
    const parents = new Map();
    const add = (childId, parent) => {
        const bucket = parents.get(childId);
        if (bucket === undefined)
            parents.set(childId, [parent]);
        else if (!bucket.some((item) => item.id === parent.id))
            bucket.push(parent);
    };
    for (const edge of edges) {
        if (edge.edgeType === 'BELONGS_TO_STAGE') {
            add(edge.sourceId, { id: edge.targetId, nodeType: 'Stage' });
        }
        else if (edge.edgeType === 'INCLUDES_BUSINESS') {
            add(edge.targetId, { id: edge.sourceId, nodeType: 'Flow' });
        }
        else if (edge.edgeType === 'USES_RULE') {
            add(edge.targetId, { id: edge.sourceId, nodeType: 'Business' });
        }
    }
    return parents;
}
/**
 * 直接の変更を、階層のエッジに沿って親へ伝える。**BigQuery を知らない純関数である。**
 *
 * 直接変わったノードは直接のまま残し、その親から上を「配下に変更」にする。直接変わった
 * ノードからも上へ辿る — 業務を直接書き換えた案件で、その業務を含む業務フローが変わらない
 * ことにはならない。1 度印を付けたノードは 2 度辿らないので、関係が輪になっていても止まる。
 *
 * 並びはノードの id の昇順である。行の順や辿った順で並べると、同じ案件を読み直すたびに
 * 並びが変わる。
 *
 * @param direct 直接変わったノード
 * @param edges 階層のエッジ（master と案件の前後の和集合）
 * @returns 直接と配下の変わったノード（id の昇順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 直接の変更を階層のエッジに沿って親へ伝え、直接と配下に変更のノードを id の順に並べる
 * @graph-connects none
 */
export function propagatePjLaneChanges(direct, edges) {
    const parents = parentsByChild(edges);
    const changed = new Map(direct.map((change) => [change.nodeId, change]));
    // まだ親を辿っていないノード。取り出す順は結果に効かない（印は「辿り着いたか」だけで決まり、
    // 並びは最後に id で揃える）ので、末尾から取り出す。
    const pending = direct.map((change) => change.nodeId);
    for (let childId = pending.pop(); childId !== undefined; childId = pending.pop()) {
        for (const parent of parents.get(childId) ?? []) {
            if (changed.has(parent.id))
                continue;
            changed.set(parent.id, {
                nodeId: parent.id,
                nodeType: parent.nodeType,
                changeKind: 'descendant',
            });
            pending.push(parent.id);
        }
    }
    return [...changed.values()].sort((left, right) => left.nodeId - right.nodeId);
}
/** BQ が返した値を整数にする。整数に読めなければ投げる。 @graph-connects none */
function readNumber(record, key) {
    const raw = record[key];
    const parsed = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isInteger(parsed)) {
        throw new Error(`spec-graph: ${key} に整数でない値が入っている: ${String(raw)}`);
    }
    return parsed;
}
/** エッジの行を読む。知らない種別の行は投げる。 @graph-connects none */
function toLaneEdges(rows) {
    return rows.flatMap((row) => {
        const record = asRecord(row);
        if (record === null)
            return [];
        const edgeType = PJ_LANE_EDGE_TYPES.find((value) => value === record.edgeType);
        if (edgeType === undefined) {
            throw new Error(`spec-graph: 変更の伝播が読めないエッジ型の行が返った: ${String(record.edgeType)}`);
        }
        return [
            {
                edgeType,
                sourceId: readNumber(record, 'sourceId'),
                targetId: readNumber(record, 'targetId'),
            },
        ];
    });
}
/**
 * 案件の変更の行から、レーン 4 型の変わったノードを読む。
 *
 * 行は呼び出し側が読んだものを受け取る（案件の変更一覧の読取が既に読んでいる）。ここが引くのは
 * 階層のエッジのクエリ 1 本だけで、ノードは読まない — 伝播に要るのは関係と、行の前後の値だけ
 * である。行に 4 型が 1 件も無ければ、エッジを引かずに空を返す。
 *
 * @param config 読取接続
 * @param rows その案件の変更の行（状態を問わず全部）
 * @returns 直接と配下の変わったノード（id の昇順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更の行と階層のエッジから、ステージ・業務フロー・業務・業務ルールの変更の種類を決めて返す
 * @graph-connects bigquery.spec_graph_edges [reads_from, via:edgeType] 階層のエッジ 3 本の読取
 */
export async function readElementPjLaneChanges(config, rows) {
    const direct = directPjLaneChanges(rows);
    if (direct.length === 0)
        return [];
    const edgeRows = await runReadQuery(config, `SELECT edgeType, sourceId, targetId
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.edges)}
WHERE graphId = @graphId AND edgeType IN UNNEST(@edgeTypes)`, { edgeTypes: [...PJ_LANE_EDGE_TYPES] });
    return propagatePjLaneChanges(direct, [...toLaneEdges(edgeRows), ...pjLaneEdgesOfRows(rows)]);
}
//# sourceMappingURL=pj-lane-changes.js.map